import { CategoryPath } from '@config/categories'

import { getCategoryIdForPath } from './getCategoryIdForPath'
import { getLoanPurposeDecoded } from './getLoanPurposeDecoded'
import { getQueryCategoryLoanFilterValues } from './getQueryCategoryLoanFilterValues'
import getScoreIdDecoded from './getScoreIdDecoded'

export interface SupportedQueryParameters {
    /**
     * The score id in case.
     */
    ScoreId?: string
    /**
     * The adgroup used for tracking.
     */
    adg?: string
    /**
     * Whether the customer came from the auto loans redirect journey.
     */
    autoLoansRedirect?: string
    /**
     * The blueshift send id used for tracking.
     */
    bsft_mid?: string
    /**
     * The shorthand for campaign
     */
    c?: string
    /**
     * The campaign
     */
    cam?: string
    /**
     * The category to display.
     */
    category?: CategoryPath
    /**
     * The customer id for displaying the scored loan results.
     */
    customerId?: string
    /**
     * The customer origin.
     */
    customerOrigin?: string
    /**
     * The customer type.
     */
    customerType?: string
    /**
     * Which loans form did the customer come through
     */
    formOrigin?: string
    /**
     * A Google ad campaign id
     */
    gad_campaignid?: string
    /**
     * The Google click id used for tracking.
     */
    gclid?: string
    /**
     * @deprecated Is the customer known?
     */
    isKnownCustomer?: string
    /**
     * Is this a new sign up?
     */
    isNewSignUp?: string
    /**
     * The journey the customer came from.
     */
    journey?: string
    /**
     * The keyword used for tracking.
     */
    k?: string
    /**
     * The keyword used for tracking.
     */
    keyword?: string
    /**
     * The loan amount used to filter the loans table.
     */
    loanAmount?: string
    /**
     * The loan id used to display the highlighted loan banner from CRM campaigns.
     */
    loanId?: string
    /**
     * The loan purpose
     */
    loanPurpose?: string
    /**
     * The loan term used to filter the loans table.
     */
    loanTerm?: string
    /**
     * The media source used for tracking.
     */
    m?: string
    /**
     * The mock id used for returning mock data
     */
    mockId?: string
    /**
     * The media source used for tracking.
     */
    pid?: string
    /**
     * The score id for displaying the scored loan results.
     */
    scoreId?: string
    /**
     * The score id encoded, was used for deep link journeys as the semi-colon
     * in the score id caused issues.
     */
    scoreIdEncoded?: string
    /**
     * The score id in lowercase.
     */
    scoreid?: string
    /**
     * The send id used for tracking.
     */
    sendId?: string
    /**
     * The sort key used to sort the loans table.
     */
    sortKey?: string
    /**
     * A utm campaign name
     */
    utm_campaign?: string
    /**
     * A utm campaign id
     */
    utm_campaign_id?: string
    /**
     * The utm content used for tracking.
     */
    utm_content?: string
    /**
     * The utm medium used for tracking marketing medium.
     */
    utm_medium?: string
    /**
     * The tracking parameter for the marketing source used in campaigns
     */
    utm_source?: string
    /**
     * The utm term used for tracking - bump
     */
    utm_term?: string
    /**
     * Where did the customer come from?
     */
    visitSource?: string
}

export function getAppStateFromQueryParameters(
    queryParameters: SupportedQueryParameters | null
) {
    if (queryParameters == null) {
        return {}
    }

    const categoryId = getCategoryIdForPath(queryParameters.category!)
    const loanFilterValues = getQueryCategoryLoanFilterValues({
        category: categoryId,
        loanAmount: queryParameters.loanAmount,
        loanTerm: queryParameters.loanTerm,
    })

    return {
        loanAmount: loanFilterValues.loanAmount,
        loanTerm: loanFilterValues.loanTerm,

        loanPurpose: getLoanPurposeDecoded(queryParameters.loanPurpose!),
        loanId: queryParameters.loanId,

        sortKey: queryParameters.sortKey,

        category: queryParameters.category as CategoryPath,
        categoryId,

        customerOrigin: queryParameters.customerOrigin,

        scoreId:
            queryParameters.scoreId ??
            queryParameters.scoreid ??
            queryParameters.ScoreId ??
            (queryParameters.scoreIdEncoded
                ? getScoreIdDecoded(queryParameters.scoreIdEncoded)
                : null),

        customerId: queryParameters.customerId,
        customerType: queryParameters.customerType,
        isKnownCustomer: queryParameters.isKnownCustomer,
        isNewSignUp: queryParameters.isNewSignUp,

        visitSource: queryParameters.visitSource,

        sendId: queryParameters.sendId ?? queryParameters.bsft_mid,
        googleClickId: queryParameters.gclid,
        keyword: queryParameters.keyword ?? queryParameters.k,
        mediaSource:
            queryParameters.utm_source ??
            queryParameters.m ??
            queryParameters.pid,
        adgroup: queryParameters.adg,
        campaign:
            queryParameters.utm_campaign ??
            queryParameters.cam ??
            queryParameters.c,
        campaignId:
            queryParameters.utm_campaign_id ?? queryParameters.gad_campaignid,
        utmContent: queryParameters.utm_content,
        utmMedium: queryParameters.utm_medium,

        autoLoansRedirect: queryParameters.autoLoansRedirect,

        mockId: queryParameters.mockId,

        formOrigin: queryParameters.formOrigin,
        journey: queryParameters.journey,
    }
}

export type QueryParametersAppState = ReturnType<
    typeof getAppStateFromQueryParameters
>
